import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Gem, Award } from "lucide-react";
import { SectionCard, TextRow, ListEditor, RowsEditor } from "@/components/Brand/editors";

export default function BrandOverviewTab({ brand, onSaved, readMode = false }) {
  const [core, setCore] = useState(brand.core || {});
  const [usps, setUsps] = useState(brand.usps || []);
  const [busy, setBusy] = useState(false);
  const setC = (k, v) => setCore((s) => ({ ...s, [k]: v }));

  const save = async (payload, opts = {}) => {
    setBusy(true);
    try {
      await api.patch(`/brands/${brand.id}`, payload);
      if (!opts.silent) toast.success("Saved");
      onSaved?.();
    } catch (e) {
      if (!opts.silent) toast.error(apiErrorMessage(e, "Save failed"));
      throw e;
    }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="brand-overview-tab">
      <SectionCard
        title="Brand core"
        icon={Gem}
        onSave={readMode ? undefined : () => save({ core })}
        busy={busy}
        testid="brand-core-section"
        autoSave={readMode ? undefined : () => save({ core }, { silent: true })}
        dirtyKey={JSON.stringify(core)}
      >
        <TextRow label="Tagline" value={core.tagline} onChange={(v) => setC("tagline", v)} testid="core-tagline" readMode={readMode} />
        <TextRow label="Mission" value={core.mission} onChange={(v) => setC("mission", v)} multiline testid="core-mission" readMode={readMode}
          help="Your reason for being — what you do, for whom, and why it matters. Keep it under 2 sentences." />
        <TextRow label="Vision" value={core.vision} onChange={(v) => setC("vision", v)} multiline testid="core-vision" readMode={readMode}
          help="The future you're building toward — the world you want to see 5–10 years out." />
        <TextRow label="Positioning statement" value={core.positioning} onChange={(v) => setC("positioning", v)} multiline testid="core-positioning" readMode={readMode}
          help="Format: For [audience], [brand] is the [category] that [key benefit], because [reason to believe]." />
        <TextRow label="Elevator pitch" value={core.elevator_pitch} onChange={(v) => setC("elevator_pitch", v)} multiline testid="core-pitch" readMode={readMode}
          help="30-second version of the brand. Say it out loud — if you stumble, tighten it." />
        <TextRow label="Brand story" value={core.story} onChange={(v) => setC("story", v)} multiline testid="core-story" readMode={readMode}
          help="Origin, turning points, what you stand for. Use `/` to add headings, quotes, or images." />
        <ListEditor label="Values" items={core.values || []} onChange={(v) => setC("values", v)} placeholder="e.g. Craftsmanship" testid="core-values" readOnly={readMode} />
      </SectionCard>
      <SectionCard
        title="USPs & differentiators"
        icon={Award}
        onSave={readMode ? undefined : () => save({ usps })}
        busy={busy}
        testid="brand-usps-section"
        hint="ranked — first is strongest"
        autoSave={readMode ? undefined : () => save({ usps }, { silent: true })}
        dirtyKey={JSON.stringify(usps)}
      >
        <RowsEditor
          columns={[{ key: "title", label: "USP" }, { key: "detail", label: "Detail", type: "textarea" }]}
          rows={usps} onChange={setUsps} addLabel="Add USP" testid="usps" readMode={readMode} />
      </SectionCard>
    </div>
  );
}
